import { Action, hasManagementRole } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchAccessProfileQueryOptions } from "@/api/accessProfile";
import { fetchAnalyticsDashboardQueryOptions } from "@/api/analytics";
import { fetchUserOrganizationsQueryOptions } from "@/api/organizations";
import { fetchTodayScheduleSessionsQueryOptions } from "@/api/schedules";
import { fetchCurrentUserQueryOptions } from "@/api/users";
import { can, useCan, useHasManagementRole } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { GreetingBlock } from "./-components/dashboard/GreetingBlock";
import { MetricCards } from "./-components/dashboard/MetricCards";
import {
	customersAtRiskQueryOptions,
	expiringEnrollmentsQueryOptions,
	NeedsAttention,
	overdueInvoicesQueryOptions,
} from "./-components/dashboard/NeedsAttention";
import { TodaySchedule } from "./-components/dashboard/TodaySchedule";
import { UpcomingBirthdays } from "./-components/dashboard/UpcomingBirthdays";
import { MobileTopAppBar } from "./-components/MobileTopAppBar";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/")({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Access profile is already cached by the parent layout loader
		const profile = await context.queryClient.ensureQueryData(
			fetchAccessProfileQueryOptions(userId, token, orgId),
		);

		// If user cannot view analytics (e.g. ORG_INSTRUCTOR_CALENDAR_ONLY),
		// redirect to the first page they can access
		if (!can(profile, Action.VIEW_ANALYTICS)) {
			if (can(profile, Action.VIEW_ATTENDANCE)) {
				throw redirect({ to: "/app/org/$orgId/attendance", params: { orgId } });
			}
			// Fallback: stay on page but let each widget degrade independently
		}

		// Preload user organizations (used by the shell studio switcher)
		context.queryClient.ensureQueryData(
			fetchUserOrganizationsQueryOptions(userId, token),
		);

		// Greeting name (§7.8) — warms the already-cached current user for the
		// mobile greeting; a cache-hit read, not a new request path.
		context.queryClient.ensureQueryData(
			fetchCurrentUserQueryOptions(userId, token),
		);

		// Prefetch the dashboard's widget queries so they overlap the route
		// transition. Each widget still owns its own `useQuery`, so a single
		// failing endpoint degrades only its card (graceful-degradation rule §6).
		// Fired without await: the page renders immediately with per-widget
		// skeletons rather than blocking navigation.
		context.queryClient.ensureQueryData(
			fetchAnalyticsDashboardQueryOptions(userId, token, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchTodayScheduleSessionsQueryOptions(userId, token, orgId),
		);

		// "Needs attention" is management-only (§role-gating) — skip its prefetch
		// for non-management users so their dashboard fires no needless (or
		// forbidden) enrollment/invoice calls.
		if (hasManagementRole(profile)) {
			context.queryClient.ensureQueryData(
				expiringEnrollmentsQueryOptions(userId, token, orgId),
			);
			context.queryClient.ensureQueryData(
				overdueInvoicesQueryOptions(userId, token, orgId),
			);
			// Top at-risk customers — only prefetch when the role holds the
			// permission, so a forbidden call never fires (graceful-degradation §6).
			if (can(profile, Action.VIEW_CUSTOMER_RISK_ANALYSIS)) {
				context.queryClient.ensureQueryData(
					customersAtRiskQueryOptions(orgId, token),
				);
			}
		}

		return { userId, token, orgId };
	},
	component: DashboardHome,
});

const headerActionClass =
	"inline-flex items-center gap-2 rounded-[10px] bg-neutral px-4 py-2.5 text-sm font-semibold text-neutral-content transition-colors hover:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

function DashboardHome() {
	const { t } = useTranslation();
	const { orgId, userId, token } = Route.useLoaderData();
	const { locale } = useDateFormatter();
	const can = useCan();

	// Access profile is cached by the parent org layout loader — a cache hit.
	const { data: profile } = useSuspenseQuery(
		fetchAccessProfileQueryOptions(userId, token, orgId),
	);
	const organizationName = profile.orgName ?? orgId;

	const canCreateEnrollment = can(Action.MANAGE_ENROLLMENTS);
	const canCreateSale = can(Action.MANAGE_PAYMENTS);
	const canCreateCustomer = can(Action.MANAGE_CUSTOMERS);
	const canViewRisk = can(Action.VIEW_CUSTOMER_RISK_ANALYSIS);
	// KPI cards + "needs attention" are management-only (Owner / Admin / Manager).
	const isManagement = useHasManagementRole();

	const todayLabel = useMemo(
		() =>
			new Intl.DateTimeFormat(locale, {
				weekday: "short",
				day: "numeric",
				month: "long",
			}).format(new Date()),
		[locale],
	);

	return (
		<div className="mx-auto max-w-[1400px] space-y-6 pb-8">
			{/* Mobile top app bar (2a): studio identity */}
			<MobileTopAppBar
				orgId={orgId}
				organizationName={organizationName}
				planSlug={profile.planSlug}
			/>

			{/* Mobile greeting block (2a) */}
			<GreetingBlock userId={userId} token={token} />

			{/* Desktop header row (1b) */}
			<header className="hidden flex-col gap-4 sm:flex-row sm:items-end sm:justify-between md:flex">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wide text-base-content/50">
						{t("dashboard.home.eyebrow", { date: todayLabel })}
					</p>
					<h2 className="mt-1 text-2xl font-extrabold tracking-tight text-base-content">
						{t("dashboard.home.headline")}
					</h2>
				</div>
				<div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
					{canCreateEnrollment && (
						<Link
							to="/app/org/$orgId/enrollments/new"
							params={{ orgId }}
							className={headerActionClass}
						>
							<Plus className="h-4 w-4" aria-hidden="true" />
							{t("dashboard.home.newEnrollment")}
						</Link>
					)}
					{canCreateSale && (
						<Link
							to="/app/org/$orgId/billing/new"
							params={{ orgId }}
							className={headerActionClass}
						>
							<Plus className="h-4 w-4" aria-hidden="true" />
							{t("dashboard.home.newSale")}
						</Link>
					)}
					{canCreateCustomer && (
						<Link
							to="/app/org/$orgId/customers/new"
							params={{ orgId }}
							className={headerActionClass}
						>
							<Plus className="h-4 w-4" aria-hidden="true" />
							{t("dashboard.home.newCustomer")}
						</Link>
					)}
				</div>
			</header>

			{/* Metric cards — management-only (Owner / Admin / Manager) */}
			{isManagement && (
				<MetricCards orgId={orgId} userId={userId} token={token} />
			)}

			{/* Two-column body: schedule + right rail */}
			<div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_380px] lg:items-start">
				<TodaySchedule orgId={orgId} userId={userId} token={token} />
				<div className="space-y-5">
					{/* "Needs attention" — management-only (Owner / Admin / Manager) */}
					{isManagement && (
						<NeedsAttention
							orgId={orgId}
							userId={userId}
							token={token}
							canViewRisk={canViewRisk}
						/>
					)}
					<UpcomingBirthdays orgId={orgId} userId={userId} token={token} />
				</div>
			</div>
		</div>
	);
}
